import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  LabelList
} from 'recharts';
import supabase from '../../supabase';
import './TopClientsRevenue.css';

const TopClientsRevenue = ({ user }) => {
  const [clientRevenueData, setClientRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRevenueData = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);

      const { data: incomeData, error: incomeError } = await supabase
        .from('income')
        .select('client_id, total_amount')
        .eq('user_id', user.id)
        .not('client_id', 'is', null);

      if (incomeError) {
        console.error('Error fetching income:', incomeError);
        setLoading(false);
        return;
      }

      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select('id, client_name')
        .eq('user_id', user.id);

      if (clientsError) {
        console.error('Error fetching clients:', clientsError);
        setLoading(false);
        return;
      }

      const revenueMap = {};
      incomeData.forEach(income => {
        if (!revenueMap[income.client_id]) {
          revenueMap[income.client_id] = 0;
        }
        revenueMap[income.client_id] += parseFloat(income.total_amount) || 0;
      });

      const chartData = Object.entries(revenueMap).map(([clientId, totalRevenue], index) => {
        const client = clientsData.find(c => c.id === clientId);
        return {
          client_name: client ? client.client_name : 'Unknown',
          total_revenue: totalRevenue,
          client_id: clientId,
          // Add a unique key to handle same amounts
          unique_key: `${clientId}-${index}`,
        };
      });

      // Sort by revenue (descending) and then by client name (ascending) for ties
      chartData.sort((a, b) => {
        if (b.total_revenue !== a.total_revenue) {
          return b.total_revenue - a.total_revenue;
        }
        return a.client_name.localeCompare(b.client_name);
      });
      
      const top10Clients = chartData.slice(0, 10);

      setClientRevenueData(top10Clients);
      setLoading(false);
    };

    fetchRevenueData();
  }, [user]);

  const formatCurrency = (value) => {
    return `₹${parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
  };

  // Get responsive margin and width values
  const getResponsiveValues = () => {
    const isMobile = window.innerWidth < 480;
    const isTablet = window.innerWidth < 768;
    
    return {
      margin: isMobile 
        ? { top: 10, right: 80, left: 5, bottom: 10 }
        : isTablet 
        ? { top: 10, right: 100, left: 10, bottom: 10 }
        : { top: 10, right: 120, left: 20, bottom: 10 },
      yAxisWidth: isMobile ? 50 : isTablet ? 70 : 100
    };
  };

  if (loading) {
    return (
      <div className="topclientsrevenue">
        <div className="topclientsrevenue-loading">
          <div className="topclientsrevenue-loading-spinner"></div>
          <p>Loading client revenue data...</p>
        </div>
      </div>
    );
  }

  const { margin, yAxisWidth } = getResponsiveValues();

  return (
    <div className="topclientsrevenue">
      <div className="topclientsrevenue-section">
        <div className="topclientsrevenue-section-header">
          <h2 className="topclientsrevenue-section-title">Top 10 Clients by Revenue</h2>
        </div>

        {clientRevenueData.length > 0 ? (
          <div className="topclientsrevenue-chart-container">
            <div className="topclientsrevenue-chart-scroll">
              <ResponsiveContainer
                width="100%"
                height={Math.max(400, clientRevenueData.length * 40)}
                minWidth={320} // reduced from 350 for better mobile fit
              >
                <BarChart
                  layout="vertical"
                  data={clientRevenueData}
                  margin={margin}
                  barCategoryGap="5%"
                >
                  <CartesianGrid strokeDasharray="0 0" horizontal={false} stroke="#e0e0e0" />
                  <XAxis
                    type="number"
                    axisLine={true}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#666' }}
                    tickFormatter={(value) => `${value}`}
                  />
                  <YAxis
  dataKey="client_name"
  type="category"
  width={yAxisWidth}
  axisLine={true}
  tickLine={false}
  tick={{ fontSize: 12, fill: '#333' }}
/>

                  <Bar
                    dataKey="total_revenue"
                    fill="#2563eb"
                    barSize={25}
                    radius={[0, 4, 4, 0]}
                  >
                    <LabelList
                      dataKey="total_revenue"
                      position="right"
                      formatter={formatCurrency}
                      style={{ fontSize: '12px', fontWeight: 500, fill: '#333' }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="topclientsrevenue-no-data">
            <p>No revenue data found for clients.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopClientsRevenue;